import React from 'react';
import { formatNPR } from '../../utils/currency';
import toast from 'react-hot-toast';
import {
  decrementOrderQuantity,
  formatBundleOrderSummary,
  getBundleExplainer,
  getBundleStockHint,
  getQuickQuantityPresets,
  incrementOrderQuantity,
  normalizeOrderQuantity,
} from '../../utils/orderQuantity';

const TRUST_ITEMS = ['Secure checkout', 'Fast shipping', 'Easy returns', 'Wholesale pricing'];

function formatRs(value) {
  const n = parseFloat(value);
  if (!Number.isFinite(n)) return '—';
  return formatNPR(n);
}

const BADGE_STYLES = {
  neutral: 'bg-slate-100 text-slate-600',
  popular: 'bg-sky-50 text-sky-800',
  value: 'bg-amber-50 text-amber-800',
  wholesale: 'bg-brand-50 text-brand-700',
};

export default function WholesalePurchaseSelector({
  moq,
  total,
  onChange,
  maxQty,
  availableStock,
  unitPrice,
  onAddToCart,
  onBuyNow,
}) {
  const explainer = getBundleExplainer(moq);
  const summary = formatBundleOrderSummary(total, moq);
  const stockHint = getBundleStockHint(moq, { max: maxQty });
  const presets = getQuickQuantityPresets(moq, { max: maxQty, maxOptions: 6 });
  const canDecrease = total > moq;
  const canIncrease =
    incrementOrderQuantity(total, moq, maxQty) !== total;
  const isOutOfStock =
    availableStock == null || availableStock < moq;
  const lineTotal =
    unitPrice != null && Number.isFinite(parseFloat(unitPrice))
      ? parseFloat(unitPrice) * total
      : null;

  const handleInputChange = (e) => {
    const raw = e.target.value;
    if (raw === '') {
      onChange(moq);
      return;
    }
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed)) {
      onChange(moq);
      return;
    }
    const normalized = normalizeOrderQuantity(parsed, {
      step: moq,
      min: moq,
      max: maxQty,
    });
    if (normalized !== parsed) {
      toast(
        moq === 1 ? 'Enter a valid quantity' : `Full bundles only (${moq} pcs each)`,
        { icon: 'ℹ️' }
      );
    }
    onChange(normalized);
  };

  return (
    <div className="space-y-2">
      {/* MOQ — one line */}
      <p className="text-xs text-slate-500 border-l-2 border-brand-500 pl-2 leading-snug">
        <span className="font-semibold text-slate-700">{explainer.shortTitle}</span>
        {moq > 1 && <span className="text-slate-400"> · min 1 bundle</span>}
      </p>

      {/* Bundle grid — compact chips */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
            Bundles
          </span>
          {stockHint && (
            <span className="text-[10px] text-slate-400 truncate max-w-[50%]">{stockHint}</span>
          )}
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-1">
          {presets.map((preset) => {
            const isActive = total === preset.quantity;
            const badge = preset.badge;
            return (
              <button
                key={preset.quantity}
                type="button"
                onClick={() => onChange(preset.quantity)}
                className={`relative flex flex-col items-center justify-center rounded-lg border px-1 py-2 min-h-[58px] transition-colors
                  ${
                    isActive
                      ? 'border-brand-600 bg-brand-600 text-white'
                      : 'border-slate-200 bg-white hover:border-brand-400'
                  }`}
              >
                {badge && (
                  <span
                    className={`absolute -top-1.5 left-1/2 -translate-x-1/2 px-1.5 py-px rounded text-[8px] font-bold uppercase leading-none whitespace-nowrap ${
                      isActive
                        ? 'bg-white text-brand-700'
                        : BADGE_STYLES[badge.variant] || BADGE_STYLES.neutral
                    }`}
                  >
                    {badge.label}
                  </span>
                )}
                <span className={`text-xs font-bold leading-tight ${isActive ? 'text-white' : 'text-slate-800'}`}>
                  {preset.primary}
                </span>
                <span className={`text-[10px] ${isActive ? 'text-brand-100' : 'text-slate-500'}`}>
                  {preset.secondary}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Qty + total + actions — single purchase row */}
      <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-2.5 sm:p-3 space-y-2">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {/* Stepper */}
          <div className="flex items-center rounded-lg border border-slate-200 bg-white h-10 shrink-0">
            <button
              type="button"
              onClick={() => onChange(decrementOrderQuantity(total, moq, moq))}
              disabled={!canDecrease}
              className="w-9 flex items-center justify-center text-slate-600 hover:bg-slate-50 disabled:opacity-30 border-r border-slate-200"
              aria-label="Decrease"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" d="M5 12h14" />
              </svg>
            </button>
            <div className="flex items-center gap-0.5 px-2 min-w-[72px] justify-center">
              <input
                type="number"
                min={moq}
                step={moq}
                value={total}
                onChange={handleInputChange}
                className="w-10 font-semibold text-base text-center bg-transparent focus:outline-none text-slate-900"
                aria-label="Quantity"
              />
              <span className="text-xs text-slate-500">pcs</span>
            </div>
            <button
              type="button"
              onClick={() => onChange(incrementOrderQuantity(total, moq, maxQty))}
              disabled={!canIncrease}
              className="w-9 flex items-center justify-center text-slate-600 hover:bg-slate-50 disabled:opacity-30 border-l border-slate-200"
              aria-label="Increase"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" d="M12 5v14M5 12h14" />
              </svg>
            </button>
          </div>

          {moq > 1 && (
            <span className="text-[11px] text-slate-500 hidden sm:inline">{summary.headline}</span>
          )}

          <div className="flex-1 min-w-[100px] text-right sm:ml-auto">
            <span className="text-[10px] text-slate-500 uppercase tracking-wide">Total</span>
            <p className="text-lg font-bold text-slate-900 leading-tight">
              {lineTotal != null ? formatRs(lineTotal) : '—'}
            </p>
            {unitPrice != null && (
              <p className="text-[10px] text-slate-400">
                {formatRs(unitPrice)} × {total}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            disabled={isOutOfStock || total < moq}
            onClick={onAddToCart}
            className="h-10 rounded-lg text-sm font-semibold text-white bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
          >
            Add to cart
          </button>
          {onBuyNow ? (
            <button
              type="button"
              disabled={isOutOfStock || total < moq}
              onClick={onBuyNow}
              className="h-10 rounded-lg text-sm font-semibold text-brand-700 bg-white border border-brand-600 hover:bg-brand-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Buy now
            </button>
          ) : (
            <div />
          )}
        </div>
      </div>

      {/* Trust — single compact row */}
      <p className="text-[10px] text-slate-400 flex flex-wrap items-center gap-x-2 gap-y-0.5">
        {TRUST_ITEMS.map((item, i) => (
          <span key={item} className="inline-flex items-center gap-1">
            {i > 0 && <span className="text-slate-300">·</span>}
            <svg className="w-3 h-3 text-brand-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            {item}
          </span>
        ))}
      </p>
    </div>
  );
}
